/**
 * Discovery > Inventory > Applications (slice D1, 3 Nov 2026)
 *
 * The applications the tenant runs, and which application each data source
 * and asset belongs to. A data source is mapped as a whole (every table or
 * file in it at once); one asset can be overridden to another application.
 * Founder Q9, 3 Nov: per data source, with a per-asset override.
 *
 * Applications are created here (works without FOCTTA Consent) or come from
 * Consent. On a tenant paired with Consent (slice D1c, founder 3 Nov 2026)
 * Consent is the only source: Add and Edit are hidden, "Sync applications"
 * pulls Consent's inventory, and a local application from before the pairing
 * is read only with "Link to a Consent application", which moves its mappings
 * there. A retired application keeps its mappings and says so. Reads need
 * discovery:read. Changes need discovery:applications:write (DPO, Compliance
 * Analyst): slice D3 makes a finding legitimate from this mapping, so it is a
 * privacy decision. Row-level security limits a unit-scoped user to their
 * units' rows and the tenant-wide ones; the controller refuses a mapping that
 * would reach assets outside them, and the page shows its reason.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, AppWindow, ChevronDown, ChevronRight, CloudDownload, Database, Link2, Loader2, Pencil, Plus, RefreshCw } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageGuide } from '@/components/ui/page-guide';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { listBusinessUnits } from '@/lib/api';
import { canAct, useCaller } from '@/lib/access';
import {
  createApplication,
  linkToConsent,
  loadApplications,
  listDataSourceAssets,
  listDataSources,
  NOT_PAIRED,
  removeLink,
  setLink,
  syncApplications,
  updateApplication,
  type Application,
  type ApplicationFields,
  type ConsentInfo,
  type DataSource,
  type DataSourceAsset,
  type SyncCounts,
} from '@/lib/applications-api';

export const WRITE_PERMISSION = 'discovery:applications:write';
export const NOT_MAPPED = '__none';
export const FOLLOW_DATA_SOURCE = '__follow';
const ALL_UNITS_TEXT = 'All units';
const OTHER_UNIT_TEXT = 'An application outside your business units';
export const RETIRED_TEXT = 'Application retired';

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : 'The request failed';
}

export function unitName(id: string | null, names: ReadonlyMap<string, string>): string {
  if (!id) return ALL_UNITS_TEXT;
  return names.get(id) ?? `Unit ${id.slice(0, 8)}`;
}

/** The unit cell: the unit, or why an application has none usable here. */
export function appUnitText(a: Pick<Application, 'businessUnitId' | 'coreBusinessUnitRef' | 'unitState'>, names: ReadonlyMap<string, string>): string {
  if (a.unitState === 'disabled') return 'Unit disabled in Discovery';
  if (a.unitState === 'not_synced' || (!a.businessUnitId && a.coreBusinessUnitRef)) return 'Unit not synced from FOCTTA Consent yet';
  return unitName(a.businessUnitId, names);
}

export function sourceText(a: Pick<Application, 'source'>, paired = false): string {
  if (a.source === 'foctta_core') return 'FOCTTA Consent';
  return paired ? 'Created in Discovery, read only' : 'Created in Discovery';
}

/** "Last synced" in the reader's own clock, or that no sync has run. */
export function lastSyncText(consent: ConsentInfo): string {
  if (!consent.lastSyncedAt) return 'Not synced yet';
  const at = new Date(consent.lastSyncedAt);
  return `Last synced ${at.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`;
}

export function syncCountsText(c: SyncCounts): string {
  return `Synced from FOCTTA Consent: ${c.added} added, ${c.updated} updated, ${c.retired} retired, ${c.unchanged} unchanged.`;
}

function normalName(n: string): string {
  return n.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/**
 * The Consent application a local one most likely is: the same name once case,
 * spaces and punctuation are set aside, else the only one whose name contains
 * the other's. A suggestion only; the operator confirms it or picks another.
 */
export function suggestConsentMatch(local: Pick<Application, 'name'>, consentApps: readonly Application[]): Application | null {
  const n = normalName(local.name);
  if (!n) return null;
  const exact = consentApps.filter((a) => normalName(a.name) === n);
  if (exact.length === 1) return exact[0]!;
  if (exact.length > 1) return null;
  const near = consentApps.filter((a) => {
    const m = normalName(a.name);
    return m.length > 0 && (m.includes(n) || n.includes(m));
  });
  return near.length === 1 ? near[0]! : null;
}

function ownerText(o: Application['applicationOwner']): string {
  if (!o) return '-';
  return [o.name, o.email].filter(Boolean).join(', ');
}

/** The application a data source row shows: its name, or why there is none to show. */
export function mappedText(ds: DataSource): string {
  if (!ds.link) return 'Not mapped';
  const name = ds.link.applicationName ?? OTHER_UNIT_TEXT;
  return ds.link.applicationStatus === 'retired' ? `${name} (${RETIRED_TEXT})` : name;
}

/**
 * Which assets a data source link decides. A link recorded for one unit
 * (repository LINK_COVERS) leaves another unit's assets unmapped, also when
 * the DPO made it (founder, 3 Nov 2026), so the row must say so rather than
 * read as if it covered the whole data source.
 */
export function linkUnitText(link: DataSource['link'], names: ReadonlyMap<string, string>): string | null {
  if (!link) return null;
  if (!link.businessUnitId) return 'Covers all units';
  return `Covers ${unitName(link.businessUnitId, names)} only`;
}

/**
 * Assets the data source link does not reach: outside the link's unit and
 * without an override of their own. Counted from the assets the reader can
 * see, so a unit-limited reader is never told about a unit they cannot see.
 */
export function uncoveredCount(link: DataSource['link'], assets: readonly DataSourceAsset[]): number {
  if (!link?.businessUnitId) return 0;
  return assets.filter((a) => !a.override && a.businessUnitId !== link.businessUnitId).length;
}

export function uncoveredText(n: number): string | null {
  if (n === 0) return null;
  return n === 1 ? '1 asset in another unit is not covered' : `${n} assets in other units are not covered`;
}

interface FormState {
  name: string;
  description: string;
  businessUnitId: string;
  applicationOwnerName: string;
  applicationOwnerEmail: string;
  dataOwnerName: string;
  dataOwnerEmail: string;
  status: 'active' | 'retired';
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  businessUnitId: '',
  applicationOwnerName: '',
  applicationOwnerEmail: '',
  dataOwnerName: '',
  dataOwnerEmail: '',
  status: 'active',
};

function formOf(a: Application): FormState {
  return {
    name: a.name,
    description: a.description ?? '',
    businessUnitId: a.businessUnitId ?? '',
    applicationOwnerName: a.applicationOwner?.name ?? '',
    applicationOwnerEmail: a.applicationOwner?.email ?? '',
    dataOwnerName: a.dataOwner?.name ?? '',
    dataOwnerEmail: a.dataOwner?.email ?? '',
    status: a.status,
  };
}

/** The request body: blank fields clear, the unit is null for every unit. */
export function fieldsOf(f: FormState, editing: boolean): ApplicationFields & { name: string } {
  return {
    name: f.name.trim(),
    description: f.description.trim() || null,
    businessUnitId: f.businessUnitId || null,
    applicationOwnerName: f.applicationOwnerName.trim() || null,
    applicationOwnerEmail: f.applicationOwnerEmail.trim() || null,
    dataOwnerName: f.dataOwnerName.trim() || null,
    dataOwnerEmail: f.dataOwnerEmail.trim() || null,
    ...(editing ? { status: f.status } : {}),
  };
}

export function Applications(): JSX.Element {
  const access = useCaller();
  const canWrite = access.status === 'ready' && canAct(WRITE_PERMISSION, access.caller);
  const [apps, setApps] = useState<Application[]>([]);
  const [consent, setConsent] = useState<ConsentInfo>(NOT_PAIRED);
  const [syncing, setSyncing] = useState(false);
  const [linking, setLinking] = useState<Application | null>(null);
  const [sources, setSources] = useState<DataSource[]>([]);
  const [unitNames, setUnitNames] = useState<Map<string, string>>(new Map());
  const [units, setUnits] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [form, setForm] = useState<{ editing: Application | null; state: FormState } | null>(null);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [a, s] = await Promise.all([loadApplications('all'), listDataSources()]);
      setApps(a.items);
      setConsent(a.consent);
      setSources(s);
    } catch (e) {
      setLoadError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Unit names need foundational:read, which not every discovery:read role
  // holds. Without it a row shows the unit's short id rather than failing.
  useEffect(() => {
    let live = true;
    listBusinessUnits({ status: 'active' })
      .then((res) => {
        if (!live) return;
        setUnitNames(new Map(res.businessUnits.map((u) => [u.id, u.name])));
        setUnits(res.businessUnits.map((u) => ({ id: u.id, name: u.name })).sort((x, y) => x.name.localeCompare(y.name)));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const activeApps = useMemo(() => apps.filter((a) => a.status === 'active'), [apps]);
  const consentApps = useMemo(() => activeApps.filter((a) => a.source === 'foctta_core'), [activeApps]);
  const paired = consent.paired;

  async function sync() {
    setSyncing(true);
    setNotice(null);
    try {
      const res = await syncApplications();
      setNotice({ kind: 'ok', text: syncCountsText(res.counts) });
      await refresh();
    } catch (e) {
      setNotice({ kind: 'error', text: errorText(e) });
      await refresh();
    } finally {
      setSyncing(false);
    }
  }

  async function confirmLink(local: Application, targetId: string): Promise<void> {
    setNotice(null);
    try {
      const res = await linkToConsent(local.id, targetId);
      const n = res.moved.dataSources + res.moved.assets;
      setNotice({
        kind: 'ok',
        text: `${local.name} is now ${res.target.name} from FOCTTA Consent: ${n} mapping${n === 1 ? '' : 's'} moved, and ${local.name} is retired.`,
      });
      setLinking(null);
      await refresh();
    } catch (e) {
      setNotice({ kind: 'error', text: errorText(e) });
    }
  }

  async function saveForm() {
    if (!form) return;
    setSaving(true);
    setNotice(null);
    try {
      const body = fieldsOf(form.state, form.editing !== null);
      if (form.editing) {
        await updateApplication(form.editing.id, body);
        setNotice({ kind: 'ok', text: `Saved ${body.name}.` });
      } else {
        await createApplication(body);
        setNotice({ kind: 'ok', text: `Added ${body.name}.` });
      }
      setForm(null);
      await refresh();
    } catch (e) {
      setNotice({ kind: 'error', text: errorText(e) });
    } finally {
      setSaving(false);
    }
  }

  async function applyLink(
    run: () => Promise<unknown>,
    okText: string,
  ): Promise<boolean> {
    setNotice(null);
    try {
      await run();
      setNotice({ kind: 'ok', text: okText });
      await refresh();
      return true;
    } catch (e) {
      setNotice({ kind: 'error', text: errorText(e) });
      return false;
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Applications"
        subtitle="The applications your organisation runs, and which application each data source and asset belongs to."
        actions={
          <div className="flex items-center gap-2">
            {canWrite && paired ? (
              <Button
                size="sm"
                onClick={() => void sync()}
                disabled={syncing || !consent.connectorActive}
                title={consent.connectorActive ? undefined : 'The FOCTTA Consent connector is not active'}
                data-action="disc.dspm_applications.a06"
                data-testid="app-sync"
              >
                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudDownload className="h-4 w-4" />} Sync applications
              </Button>
            ) : null}
            {canWrite && !paired ? (
              <Button
                size="sm"
                onClick={() => setForm({ editing: null, state: EMPTY_FORM })}
                data-action="disc.dspm_applications.a01"
                data-testid="app-add"
              >
                <Plus className="h-4 w-4" /> Add application
              </Button>
            ) : null}
            <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading} aria-label="Refresh">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </Button>
          </div>
        }
      />

      <PageGuide id="applications">
        Map a data source to an application and every table or file in it belongs to that application. Override a single
        asset when it belongs to another application. When your organisation is paired with FOCTTA Consent, applications
        come from its Application Inventory and only the mapping is set here; otherwise you add them here.
      </PageGuide>

      {paired ? <ConsentBanner consent={consent} /> : null}

      {notice ? (
        <div
          role={notice.kind === 'error' ? 'alert' : 'status'}
          className={notice.kind === 'error' ? 'mb-4 rounded-md border border-sev-high/40 p-3 text-sm' : 'mb-4 rounded-md border p-3 text-sm'}
          data-testid={notice.kind === 'error' ? 'app-error' : 'app-ok'}
        >
          {notice.text}
        </div>
      ) : null}

      {form ? (
        <ApplicationForm
          editing={form.editing}
          state={form.state}
          units={units}
          saving={saving}
          onChange={(state) => setForm({ ...form, state })}
          onCancel={() => setForm(null)}
          onSave={() => void saveForm()}
        />
      ) : null}

      {loadError ? (
        <GlassCard>
          <div className="flex items-start gap-2 p-4 text-sm" role="alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-sev-high" />
            <span className="flex-1">{loadError}</span>
            <Button size="sm" variant="glass" onClick={() => void refresh()}>
              Retry
            </Button>
          </div>
        </GlassCard>
      ) : loading && apps.length === 0 && sources.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading applications...
        </div>
      ) : (
        <div className="space-y-4">
          <ApplicationsTable
            apps={apps}
            unitNames={unitNames}
            canWrite={canWrite}
            paired={paired}
            onEdit={(a) => setForm({ editing: a, state: formOf(a) })}
            onLink={(a) => setLinking(a)}
          />
          {linking ? (
            <LinkToConsentForm
              local={linking}
              consentApps={consentApps}
              onCancel={() => setLinking(null)}
              onConfirm={(targetId) => confirmLink(linking, targetId)}
            />
          ) : null}
          <DataSourcesTable
            sources={sources}
            activeApps={activeApps}
            unitNames={unitNames}
            canWrite={canWrite}
            onMap={(ds, appId) => {
              const name = activeApps.find((a) => a.id === appId)?.name ?? 'the application';
              return applyLink(
                () => setLink({ scope: 'data_source', dataSourceKey: ds.dataSourceKey }, appId),
                `${ds.dataSourceKey} now belongs to ${name}: ${ds.assetCount} asset${ds.assetCount === 1 ? '' : 's'}.`,
              );
            }}
            onUnmap={(ds) =>
              applyLink(() => removeLink(ds.link!.id), `${ds.dataSourceKey} is no longer mapped.`)
            }
            onOverride={(asset, appId) => {
              const name = activeApps.find((a) => a.id === appId)?.name ?? 'the application';
              return applyLink(() => setLink({ scope: 'asset', assetId: asset.assetId }, appId), `${asset.displayName} now belongs to ${name}.`);
            }}
            onClearOverride={(asset) =>
              applyLink(() => removeLink(asset.override!.linkId), `${asset.displayName} follows its data source again.`)
            }
          />
        </div>
      )}
    </PageContainer>
  );
}

function ApplicationForm({
  editing,
  state,
  units,
  saving,
  onChange,
  onCancel,
  onSave,
}: {
  editing: Application | null;
  state: FormState;
  units: ReadonlyArray<{ id: string; name: string }>;
  saving: boolean;
  onChange: (s: FormState) => void;
  onCancel: () => void;
  onSave: () => void;
}): JSX.Element {
  const field = (key: keyof FormState, label: string, type = 'text') => (
    <label className="block text-xs">
      <span className="text-muted-foreground">{label}</span>
      <input
        type={type}
        aria-label={label}
        value={state[key]}
        onChange={(e) => onChange({ ...state, [key]: e.target.value })}
        className="mt-0.5 w-full rounded border bg-background px-2 py-1 text-sm"
      />
    </label>
  );
  return (
    <GlassCard className="mb-4" data-testid="app-form">
      <CardHeader>
        <CardTitle>{editing ? `Edit ${editing.name}` : 'Add application'}</CardTitle>
        <CardDescription>Owners are optional here; applications from FOCTTA Consent carry their own.</CardDescription>
      </CardHeader>
      <div className="grid gap-3 px-6 pb-6 sm:grid-cols-2">
        {field('name', 'Name')}
        <label className="block text-xs">
          <span className="text-muted-foreground">Business unit</span>
          <select
            aria-label="Business unit"
            value={state.businessUnitId}
            onChange={(e) => onChange({ ...state, businessUnitId: e.target.value })}
            className="mt-0.5 w-full rounded border bg-background px-2 py-1 text-sm"
          >
            <option value="">{ALL_UNITS_TEXT}</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
        <div className="sm:col-span-2">{field('description', 'Description')}</div>
        {field('applicationOwnerName', 'Application owner')}
        {field('applicationOwnerEmail', 'Application owner email', 'email')}
        {field('dataOwnerName', 'Data owner')}
        {field('dataOwnerEmail', 'Data owner email', 'email')}
        {editing ? (
          <label className="block text-xs">
            <span className="text-muted-foreground">Status</span>
            <select
              aria-label="Status"
              value={state.status}
              onChange={(e) => onChange({ ...state, status: e.target.value as FormState['status'] })}
              className="mt-0.5 w-full rounded border bg-background px-2 py-1 text-sm"
            >
              <option value="active">Active</option>
              <option value="retired">Retired</option>
            </select>
          </label>
        ) : null}
        <div className="flex items-end justify-end gap-2 sm:col-span-2">
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={onSave} disabled={saving || state.name.trim() === ''} data-testid="app-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </div>
    </GlassCard>
  );
}

function ConsentBanner({ consent }: { consent: ConsentInfo }): JSX.Element {
  return (
    <div className="mb-4 rounded-md border p-3 text-sm" role="status" data-testid="consent-banner">
      <span className="font-medium">Applications come from FOCTTA Consent.</span>{' '}
      <span data-testid="consent-last-sync">{lastSyncText(consent)}.</span>{' '}
      Add or change an application in Consent&apos;s Application Inventory, then press Sync applications. Which application
      each data source and asset belongs to is set here.
      {!consent.connectorActive ? (
        <span className="mt-1 block text-sev-high" data-testid="consent-connector-inactive">
          The FOCTTA Consent connector is not active, so a sync cannot run now.
        </span>
      ) : null}
      {consent.lastSyncOutcome === 'failed' && consent.lastSyncError ? (
        <span className="mt-1 block text-sev-high" data-testid="consent-last-error">
          The last sync failed: {consent.lastSyncError}
        </span>
      ) : null}
    </div>
  );
}

function LinkToConsentForm({
  local,
  consentApps,
  onCancel,
  onConfirm,
}: {
  local: Application;
  consentApps: readonly Application[];
  onCancel: () => void;
  onConfirm: (targetId: string) => Promise<void>;
}): JSX.Element {
  const suggestion = useMemo(() => suggestConsentMatch(local, consentApps), [local, consentApps]);
  const [choice, setChoice] = useState(suggestion?.id ?? '');
  const [busy, setBusy] = useState(false);
  const mapped = local.dataSourceCount + local.assetOverrideCount;
  return (
    <GlassCard data-testid="link-form">
      <CardHeader>
        <CardTitle>Link {local.name} to a Consent application</CardTitle>
        <CardDescription>
          {mapped === 0 ? 'It has no mappings.' : `Its ${mapped} mapping${mapped === 1 ? '' : 's'} move to the Consent application.`}{' '}
          {local.name} is then retired here. Nothing is deleted.
          {suggestion ? ` Suggested by name: ${suggestion.name}.` : ' No Consent application has a matching name; choose one.'}
        </CardDescription>
      </CardHeader>
      <div className="flex flex-wrap items-center gap-2 px-6 pb-6 text-xs">
        <select
          aria-label={`Consent application for ${local.name}`}
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          className="rounded border bg-background px-2 py-1 text-xs"
        >
          <option value="">Choose a Consent application</option>
          {consentApps.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!choice || busy}
          onClick={async () => {
            setBusy(true);
            await onConfirm(choice);
            setBusy(false);
          }}
          data-testid="link-confirm"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Link and move mappings
        </Button>
      </div>
    </GlassCard>
  );
}

function ApplicationsTable({
  apps,
  unitNames,
  canWrite,
  paired,
  onEdit,
  onLink,
}: {
  apps: Application[];
  unitNames: ReadonlyMap<string, string>;
  canWrite: boolean;
  paired: boolean;
  onEdit: (a: Application) => void;
  onLink: (a: Application) => void;
}): JSX.Element {
  const names = useMemo(() => new Map(apps.map((a) => [a.id, a.name])), [apps]);
  return (
    <GlassCard data-testid="apps-table">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AppWindow className="h-4 w-4 text-foreground/60" /> Applications
        </CardTitle>
        <CardDescription>
          {apps.length} application{apps.length === 1 ? '' : 's'}
        </CardDescription>
      </CardHeader>
      {apps.length === 0 ? (
        <div className="px-6 pb-6 text-sm text-muted-foreground" data-testid="apps-empty">
          {paired
            ? 'No applications yet. Press Sync applications to bring them from FOCTTA Consent.'
            : 'No applications yet. Add one here, or pair with FOCTTA Consent and they come from there.'}
        </div>
      ) : (
        <div className="overflow-x-auto px-6 pb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="pb-3 pr-4 font-medium">Application</th>
                <th className="pb-3 pr-4 font-medium">Source</th>
                <th className="pb-3 pr-4 font-medium">Business unit</th>
                <th className="pb-3 pr-4 font-medium">Application owner</th>
                <th className="pb-3 pr-4 font-medium">Data owner</th>
                <th className="pb-3 pr-4 font-medium">Mapped</th>
                <th className="pb-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.id} className="border-b align-top last:border-0" data-testid={`app-row-${a.id}`}>
                  <td className="py-3 pr-4 font-medium">
                    {a.name}
                    {a.status === 'retired' ? (
                      <Badge variant="secondary" className="ml-2 text-[10px]">
                        {RETIRED_TEXT}
                      </Badge>
                    ) : null}
                    {a.linkedApplicationId ? (
                      <span className="block text-xs font-normal text-muted-foreground" data-testid={`app-linked-${a.id}`}>
                        Linked to {names.get(a.linkedApplicationId) ?? 'a FOCTTA Consent application'}
                      </span>
                    ) : null}
                    {a.description ? <span className="block text-xs font-normal text-muted-foreground">{a.description}</span> : null}
                  </td>
                  <td className="py-3 pr-4 text-xs">{sourceText(a, paired)}</td>
                  <td className="py-3 pr-4 text-xs">
                    {appUnitText(a, unitNames)}
                  </td>
                  <td className="py-3 pr-4 text-xs">{ownerText(a.applicationOwner)}</td>
                  <td className="py-3 pr-4 text-xs">{ownerText(a.dataOwner)}</td>
                  <td className="py-3 pr-4 text-xs">
                    {a.dataSourceCount} data source{a.dataSourceCount === 1 ? '' : 's'}
                    {a.assetOverrideCount > 0 ? `, ${a.assetOverrideCount} asset override${a.assetOverrideCount === 1 ? '' : 's'}` : ''}
                  </td>
                  <td className="py-3 text-right">
                    {canWrite && a.source === 'local' && !paired ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onEdit(a)}
                        aria-label={`Edit ${a.name}`}
                        data-action="disc.dspm_applications.a02"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </Button>
                    ) : null}
                    {canWrite && a.source === 'local' && paired && a.status === 'active' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onLink(a)}
                        aria-label={`Link ${a.name} to a Consent application`}
                        data-action="disc.dspm_applications.a07"
                      >
                        <Link2 className="h-3.5 w-3.5" /> Link to a Consent application
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </GlassCard>
  );
}

function DataSourcesTable({
  sources,
  activeApps,
  unitNames,
  canWrite,
  onMap,
  onUnmap,
  onOverride,
  onClearOverride,
}: {
  sources: DataSource[];
  activeApps: Application[];
  unitNames: ReadonlyMap<string, string>;
  canWrite: boolean;
  onMap: (ds: DataSource, appId: string) => Promise<boolean>;
  onUnmap: (ds: DataSource) => Promise<boolean>;
  onOverride: (a: DataSourceAsset, appId: string) => Promise<boolean>;
  onClearOverride: (a: DataSourceAsset) => Promise<boolean>;
}): JSX.Element {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <GlassCard data-testid="sources-table">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-4 w-4 text-foreground/60" /> Data sources
        </CardTitle>
        <CardDescription>A data source is one database, bucket, file server or device, with every asset in it.</CardDescription>
      </CardHeader>
      {sources.length === 0 ? (
        <div className="px-6 pb-6 text-sm text-muted-foreground" data-testid="sources-empty">
          No data sources yet. They appear once a scan or catalogue crawl has found assets.
        </div>
      ) : (
        <div className="overflow-x-auto px-6 pb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="pb-3 pr-4 font-medium">Data source</th>
                <th className="pb-3 pr-4 font-medium">Assets</th>
                <th className="pb-3 font-medium">Application</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((ds) => (
                <DataSourceRow
                  key={ds.dataSourceKey}
                  ds={ds}
                  expanded={open === ds.dataSourceKey}
                  onToggle={() => setOpen(open === ds.dataSourceKey ? null : ds.dataSourceKey)}
                  activeApps={activeApps}
                  unitNames={unitNames}
                  canWrite={canWrite}
                  onMap={onMap}
                  onUnmap={onUnmap}
                  onOverride={onOverride}
                  onClearOverride={onClearOverride}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </GlassCard>
  );
}

function AppSelect({
  label,
  value,
  onChange,
  apps,
  emptyLabel,
  emptyValue,
  currentLabel,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  apps: Application[];
  emptyLabel: string;
  emptyValue: string;
  /** Shown for a current value the list does not offer (a retired application keeps its mappings). */
  currentLabel?: string | null;
}): JSX.Element {
  const offered = value === emptyValue || apps.some((a) => a.id === value);
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded border bg-background px-2 py-1 text-xs"
    >
      <option value={emptyValue}>{emptyLabel}</option>
      {!offered ? (
        <option value={value} disabled>
          {currentLabel ?? OTHER_UNIT_TEXT}
        </option>
      ) : null}
      {apps.map((a) => (
        <option key={a.id} value={a.id}>
          {a.name}
        </option>
      ))}
    </select>
  );
}

function DataSourceRow({
  ds,
  expanded,
  onToggle,
  activeApps,
  unitNames,
  canWrite,
  onMap,
  onUnmap,
  onOverride,
  onClearOverride,
}: {
  ds: DataSource;
  expanded: boolean;
  onToggle: () => void;
  activeApps: Application[];
  unitNames: ReadonlyMap<string, string>;
  canWrite: boolean;
  onMap: (ds: DataSource, appId: string) => Promise<boolean>;
  onUnmap: (ds: DataSource) => Promise<boolean>;
  onOverride: (a: DataSourceAsset, appId: string) => Promise<boolean>;
  onClearOverride: (a: DataSourceAsset) => Promise<boolean>;
}): JSX.Element {
  const current = ds.link?.applicationId ?? NOT_MAPPED;
  const [choice, setChoice] = useState(current);
  const [busy, setBusy] = useState(false);
  // Known once the reader opens the data source; the list route has no per-unit count.
  const [loadedAssets, setLoadedAssets] = useState<DataSourceAsset[] | null>(null);
  useEffect(() => setChoice(current), [current]);
  const changed = choice !== current;
  const coversText = linkUnitText(ds.link, unitNames);
  const uncoveredNote = loadedAssets ? uncoveredText(uncoveredCount(ds.link, loadedAssets)) : null;

  async function save() {
    setBusy(true);
    if (choice === NOT_MAPPED) await onUnmap(ds);
    else await onMap(ds, choice);
    setBusy(false);
  }

  return (
    <>
      <tr className="border-b align-top" data-testid={`source-row-${ds.dataSourceKey}`}>
        <td className="py-3 pr-4">
          <button
            type="button"
            onClick={onToggle}
            className="inline-flex items-center gap-1 text-left font-medium hover:underline"
            aria-expanded={expanded}
            aria-label={`Assets of ${ds.dataSourceKey}`}
          >
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <span className="break-all">{ds.dataSourceKey}</span>
          </button>
          <span className="block text-xs text-muted-foreground">{ds.sourceType}</span>
        </td>
        <td className="py-3 pr-4 text-xs">
          {ds.assetCount}
          {ds.overrideCount > 0 ? <span className="block text-muted-foreground">{ds.overrideCount} overridden</span> : null}
        </td>
        <td className="py-3 text-xs">
          {canWrite ? (
            <div className="flex flex-wrap items-center gap-2">
              <AppSelect
                label={`Application of ${ds.dataSourceKey}`}
                value={choice}
                onChange={setChoice}
                apps={activeApps}
                emptyLabel="Not mapped"
                emptyValue={NOT_MAPPED}
                currentLabel={ds.link ? mappedText(ds) : null}
              />
              {ds.link && !ds.link.applicationName ? <span className="text-muted-foreground">{OTHER_UNIT_TEXT}</span> : null}
              {ds.link?.applicationStatus === 'retired' ? (
                <span className="text-sev-high" data-testid={`source-retired-${ds.dataSourceKey}`}>
                  {RETIRED_TEXT}
                </span>
              ) : null}
              <Button
                size="sm"
                variant="glass"
                disabled={!changed || busy}
                onClick={() => void save()}
                data-action="disc.dspm_applications.a03 disc.dspm_applications.a05"
                data-testid={`source-save-${ds.dataSourceKey}`}
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Save
              </Button>
            </div>
          ) : (
            <span data-testid={`source-app-${ds.dataSourceKey}`}>{mappedText(ds)}</span>
          )}
          {coversText ? (
            <span className="mt-1 block text-muted-foreground" data-testid={`source-unit-${ds.dataSourceKey}`}>
              {coversText}
            </span>
          ) : null}
          {uncoveredNote ? (
            <span className="mt-1 flex items-center gap-1 text-sev-high" data-testid={`source-uncovered-${ds.dataSourceKey}`}>
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {uncoveredNote}
            </span>
          ) : null}
        </td>
      </tr>
      {expanded ? (
        <tr className="border-b">
          <td colSpan={3} className="pb-3">
            <AssetList
              ds={ds}
              activeApps={activeApps}
              unitNames={unitNames}
              canWrite={canWrite}
              onOverride={onOverride}
              onClearOverride={onClearOverride}
              onLoaded={setLoadedAssets}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function AssetList({
  ds,
  activeApps,
  unitNames,
  canWrite,
  onOverride,
  onClearOverride,
  onLoaded,
}: {
  ds: DataSource;
  activeApps: Application[];
  unitNames: ReadonlyMap<string, string>;
  canWrite: boolean;
  onOverride: (a: DataSourceAsset, appId: string) => Promise<boolean>;
  onClearOverride: (a: DataSourceAsset) => Promise<boolean>;
  onLoaded: (assets: DataSourceAsset[]) => void;
}): JSX.Element {
  const [assets, setAssets] = useState<DataSourceAsset[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    setError(null);
    try {
      const rows = await listDataSourceAssets(ds.dataSourceKey);
      setAssets(rows);
      onLoaded(rows);
    } catch (e) {
      setError(errorText(e));
    }
  }, [ds.dataSourceKey, onLoaded]);
  // Reload when the data source's mapping changes, so "follows the data source" stays true.
  useEffect(() => {
    void load();
  }, [load, ds.link?.applicationId, ds.overrideCount]);

  if (error) return <div className="ml-5 text-xs text-sev-high" role="alert">{error}</div>;
  if (!assets) {
    return (
      <div className="ml-5 flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading assets...
      </div>
    );
  }
  return (
    <table className="ml-5 w-[calc(100%-1.25rem)] text-xs" data-testid={`assets-of-${ds.dataSourceKey}`}>
      <thead>
        <tr className="text-left uppercase tracking-wide text-muted-foreground">
          <th className="py-2 pr-4 font-medium">Asset</th>
          <th className="py-2 pr-4 font-medium">Business unit</th>
          <th className="py-2 pr-4 font-medium">Belongs to</th>
          <th className="py-2 font-medium">Override</th>
        </tr>
      </thead>
      <tbody>
        {assets.map((a) => (
          <AssetRow
            key={a.assetId}
            asset={a}
            activeApps={activeApps}
            unitNames={unitNames}
            canWrite={canWrite}
            onOverride={async (appId) => {
              if (await onOverride(a, appId)) await load();
            }}
            onClear={async () => {
              if (await onClearOverride(a)) await load();
            }}
          />
        ))}
      </tbody>
    </table>
  );
}

export function belongsToText(a: DataSourceAsset): string {
  if (!a.effective) return 'No application';
  const name = a.effective.applicationName ?? OTHER_UNIT_TEXT;
  const how = a.effective.via === 'asset' ? 'overridden' : 'from the data source';
  return a.effective.applicationStatus === 'retired' ? `${name} (${how}, ${RETIRED_TEXT.toLowerCase()})` : `${name} (${how})`;
}

function AssetRow({
  asset,
  activeApps,
  unitNames,
  canWrite,
  onOverride,
  onClear,
}: {
  asset: DataSourceAsset;
  activeApps: Application[];
  unitNames: ReadonlyMap<string, string>;
  canWrite: boolean;
  onOverride: (appId: string) => Promise<void>;
  onClear: () => Promise<void>;
}): JSX.Element {
  const current = asset.override?.applicationId ?? FOLLOW_DATA_SOURCE;
  const [choice, setChoice] = useState(current);
  const [busy, setBusy] = useState(false);
  useEffect(() => setChoice(current), [current]);

  async function apply() {
    setBusy(true);
    if (choice === FOLLOW_DATA_SOURCE) await onClear();
    else await onOverride(choice);
    setBusy(false);
  }

  return (
    <tr className="border-t align-top" data-testid={`asset-row-${asset.assetId}`}>
      <td className="py-2 pr-4">
        <span className="font-medium">{asset.displayName}</span>
        {asset.ref ? <span className="block break-all text-muted-foreground">{asset.ref}</span> : null}
      </td>
      <td className="py-2 pr-4">{unitName(asset.businessUnitId, unitNames)}</td>
      <td className="py-2 pr-4" data-testid={`asset-belongs-${asset.assetId}`}>
        {belongsToText(asset)}
      </td>
      <td className="py-2">
        {canWrite ? (
          <div className="flex flex-wrap items-center gap-2">
            <AppSelect
              label={`Override for ${asset.displayName}`}
              value={choice}
              onChange={setChoice}
              apps={activeApps}
              emptyLabel="Follow the data source"
              emptyValue={FOLLOW_DATA_SOURCE}
              currentLabel={
                asset.override
                  ? `${asset.override.applicationName ?? OTHER_UNIT_TEXT}${asset.override.applicationStatus === 'retired' ? ` (${RETIRED_TEXT})` : ''}`
                  : null
              }
            />
            <Button
              size="sm"
              variant="glass"
              disabled={choice === current || busy}
              onClick={() => void apply()}
              data-action="disc.dspm_applications.a04 disc.dspm_applications.a05"
              data-testid={`asset-apply-${asset.assetId}`}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Apply
            </Button>
          </div>
        ) : asset.override ? (
          'Overridden'
        ) : (
          '-'
        )}
      </td>
    </tr>
  );
}
